import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router";
import Button from "@mui/material/Button";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { ActionLog, User, useUserData } from "../../hooks/useUserData";
import { useDialog } from "../../hooks/useDialog";
import { Alert } from "../../components/admin/Alert";
import { AddUserDialog, BulkAddUserDialog } from "../../components/admin/UserDialogs";
import { BulkUserInput } from "../../utils/csvUtils";
import { TableLayout } from "../../components/layout/TableLayout";
import { GridColDef } from "@mui/x-data-grid";
import { DialogTable } from "../../components/layout/DialogTable";
import { formatDate } from "../../util/datetime";
import { colors } from "../../theme/colors";

export default function UserList() {
  const navigate = useNavigate();

  // カスタムフックの使用
  const {
    users,
    lectures,
    actionLogs,
    fetchUsers,
    fetchActionLogs,
    createUser,
    bulkCreateUsers,
    checkExistingEmails
  } = useUserData();

  const userAddDialog = useDialog();
  const bulkAddDialog = useDialog();
  const actionLogDialog = useDialog();

  // 選択したユーザー情報
  const [selectedUser, setSelectedUser] = useState<{ id: string; userName: string; email: string } | null>(null);

  // アラート状態
  const [alert, setAlert] = useState<{ message: string; severity: "success" | "error" }>({ message: "", severity: "success" });

  // 初期データ読み込み
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // アクションログを開く
  const handleOpenActionLog = async (id: string, userName: string, email: string) => {
    setSelectedUser({ id, userName, email });
    await fetchActionLogs(id);
    actionLogDialog.openDialog();
  };

  // ユーザー追加処理
  const handleAddUser = async (userName: string, email: string, password: string, lectureIds: string[]) => {
    const result = await createUser(userName, email, password, lectureIds);

    if (result === "uc1") {
      setAlert({
        message: "受講者が正常に追加されました。",
        severity: "success"
      });
      userAddDialog.closeDialog();
      fetchUsers();
    } else if (result === "uc2") {
      setAlert({ message: "そのメールアドレスは既に登録されています。", severity: "error" });
    } else {
      setAlert({ message: "受講者を追加するのに失敗しました。", severity: "error" });
    }
  };

  // CSV 一括登録(結果はダイアログ内に表示するため、ここでは API 呼び出しと一覧再取得のみ)
  const handleBulkRegister = (users: BulkUserInput[], lectureIds: string[]) =>
    bulkCreateUsers(users, lectureIds);

  return (
    <TableLayout
      header={{
        title: "受講者一覧",
        buttons: [
          {
            label: "受講者追加",
            icon: <PersonAddAltOutlinedIcon sx={{ mr: 1 }} />,
            onClick: userAddDialog.openDialog
          },
          {
            label: "一括登録",
            icon: <PersonAddAltOutlinedIcon sx={{ mr: 1 }} />,
            onClick: bulkAddDialog.openDialog
          }
        ]
      }}
      table={{
        data: users,
        columns: buildColumns(navigate, handleOpenActionLog),
        getRowKey: (user) => user.id,
        ariaLabel: "user list table"
      }}
    >
      {/* ダイアログ */}
      <DialogTable
        open={actionLogDialog.isOpen}
        onClose={actionLogDialog.closeDialog}
        title={`ユーザ名：${selectedUser?.userName}（メールアドレス：${selectedUser?.email}）`}
        table={{
          data: actionLogs,
          columns: actionLogColumns,
          getRowKey: (log) => log.id,
        }}
      />

      <AddUserDialog
        open={userAddDialog.isOpen}
        onClose={userAddDialog.closeDialog}
        lectures={lectures}
        onAddUser={handleAddUser}
      />

      <BulkAddUserDialog
        open={bulkAddDialog.isOpen}
        onClose={bulkAddDialog.closeDialog}
        lectures={lectures}
        onBulkRegister={handleBulkRegister}
        onCheckExisting={checkExistingEmails}
        onCompleted={fetchUsers}
      />
      {alert.message !== "" && (
        <Alert
          key="active-alert"
          open={true}
          severity={alert.severity as "success" | "error"}
          message={alert.message}
          onClose={() => setAlert({ message: "", severity: "success" })}
        />
      )}
    </TableLayout>
  );
}

function buildColumns(navigate: NavigateFunction, handleOpenActionLog: (id: string, userName: string, email: string) => Promise<void>): GridColDef<User>[] {
  return [
    {
      field: "userName",
      headerName: "ユーザー名",
      flex: 2,
      renderCell: (param) => (
        <>{param.row.userName}</>
      )
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 2,
      renderCell: (param) => (
        <>{param.row.email}</>
      )
    },
    {
      field: "lectures",
      headerName: "登録講座",
      flex: 3,
      renderCell: (param) => (
        <>{param.row.lectures.map((lecture) => lecture.name).join(", ")}</>
      )
    },
    {
      field: "history",
      headerName: "受講履歴",
      flex: 1.5,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (param) => (
        <Button
          variant="contained"
          onClick={() => navigate(`/users/${param.row.id}/lectures`)}
          sx={{
            borderRadius: "20px",
            backgroundColor: colors.accent,
            "&:hover": {
              backgroundColor: colors.accent,
            },
          }}
        >
          <TaskOutlinedIcon />
        </Button>
      )
    },
    {
      field: "logs",
      headerName: "操作履歴",
      flex: 1.5,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (param) => (
        <Button
          variant="contained"
          onClick={() => handleOpenActionLog(param.row.id, param.row.userName, param.value?.email)}
          sx={{
            borderRadius: "20px",
            backgroundColor: colors.accent,
            "&:hover": {
              backgroundColor: colors.accent,
            },
          }}
        >
          <AccessTimeIcon />
        </Button>
      )
    }
  ];
}

const actionLogColumns: GridColDef<ActionLog>[] = [
  { field: "action",
     headerName: "アクション",
     flex: 2,
      renderCell: (param) => (
        <>{param.row.action}</>
      ) 
     },
  { field: "timestamp",
     headerName: "操作日時",
     flex: 2,
     renderCell: (param) => (
        <>{formatDate(param.row.createdAt)}</>
      ),
  },
  { field: "details",
     headerName: "詳細",
     flex: 4,
     renderCell: (param) => (
       <>{param.row.detail}</>
     )
   }
];